/**
 * Inline Accept / Reject / Respond actions for one row of the Submitted
 * Appeals table.
 *
 * CR-7169: responding to an appeal used to mean opening a modal for every
 * single row, which during a real appeals run is dozens of modals. Accept is
 * now one click; Reject asks for a reason inline first, because the supplier
 * reads it and a blank rejection is the black-box problem the ticket names.
 * Split into its own file to keep AppealsSubTab under the 500-line limit.
 *
 * Both inline actions go through the same per-appeal respond endpoint the
 * Respond modal already uses, so they inherit its authority checks exactly and
 * work regardless of the bulk-respond platform toggle.
 *
 * The row is never updated optimistically: on success the list refetches, and
 * on failure the row is left reading "awaiting response" — which is the truth
 * until the server says otherwise — with the server's reason in a toast.
 *
 * @link AppealsSubTab for the table that renders this in the pinned Action cell.
 */
import { useState } from 'react';
import { purchasingAPI, DeliveryAppeal } from '../../../../lib/api';
import { getApiErrorMessage } from '../../../../utils/errorUtils';
import { Check, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

interface AppealInlineActionsProps {
  appeal: DeliveryAppeal;
  /** Customer-side viewer — only they may respond to a supplier's appeal. */
  canRespond: boolean;
  /** Opens the full response form (Accept / Query / Reject with attachments). */
  onRespond: (appealId: number) => void;
  /** Called after a successful inline response so the list and tab counts refetch. */
  onResponded: () => void;
}

const BTN = 'px-2 py-1 text-xs rounded border disabled:opacity-50 disabled:cursor-not-allowed';

/**
 * The contents of the pinned Action cell for a single appeal row.
 *
 * @param appeal - the row's appeal
 * @param canRespond - customer-side viewer; suppliers never see an action here
 * @param onRespond - opens the full response modal
 * @param onResponded - refetch trigger, bumped after a successful inline action
 * @returns the three action buttons, the inline reject prompt, or a placeholder
 */
export default function AppealInlineActions({
  appeal,
  canRespond,
  onRespond,
  onResponded,
}: AppealInlineActionsProps) {
  const [busy, setBusy] = useState<'accept' | 'reject' | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  // Same eligibility the bulk checkboxes and the Respond button use, so the
  // actions can never disagree about who may act on which row.
  const awaitingResponse = appeal.status === 'awaiting_response' && !appeal.is_customer_appeal;
  if (!canRespond || !awaitingResponse) {
    return <span className="text-muted-foreground">-</span>;
  }

  const respond = async (action: 'accept' | 'reject', note?: string) => {
    setBusy(action);
    try {
      await purchasingAPI.respondToDeliveryAppeal(
        appeal.id,
        note ? { action, response_note: note } : { action },
      );
      toast.success(`Appeal #${appeal.id} ${action === 'accept' ? 'accepted' : 'rejected'}`);
      setRejecting(false);
      setReason('');
      onResponded();
    } catch (err: unknown) {
      console.error(`Failed to ${action} appeal ${appeal.id}:`, err);
      toast.error(getApiErrorMessage(err, `Failed to ${action} appeal #${appeal.id}`));
    } finally {
      setBusy(null);
    }
  };

  const cancelReject = () => {
    setRejecting(false);
    setReason('');
  };

  if (rejecting) {
    const trimmedReason = reason.trim();
    return (
      <div className="flex items-center gap-1">
        <input
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && trimmedReason) void respond('reject', trimmedReason);
            if (e.key === 'Escape') cancelReject();
          }}
          placeholder="Reason (required)"
          disabled={busy !== null}
          aria-label={`Reason for rejecting appeal ${appeal.id}`}
          className="w-[110px] px-2 py-1 text-xs rounded border border-input bg-background"
        />
        <button
          onClick={() => void respond('reject', trimmedReason)}
          disabled={!trimmedReason || busy !== null}
          title={trimmedReason ? 'Send rejection' : 'A reason is required to reject'}
          aria-label="Confirm rejection"
          className={`${BTN} border-red-200 bg-red-50 text-red-700 hover:bg-red-100`}
        >
          {busy === 'reject' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
        </button>
        <button
          onClick={cancelReject}
          disabled={busy !== null}
          title="Cancel"
          aria-label="Cancel rejection"
          className={`${BTN} border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100`}
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => void respond('accept')}
        disabled={busy !== null}
        title="Accept this appeal"
        className={`${BTN} border-green-200 bg-green-50 text-green-700 hover:bg-green-100`}
      >
        {busy === 'accept' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Accept'}
      </button>
      <button
        onClick={() => setRejecting(true)}
        disabled={busy !== null}
        title="Reject this appeal — a reason is required"
        className={`${BTN} border-red-200 bg-red-50 text-red-700 hover:bg-red-100`}
      >
        Reject
      </button>
      <button
        onClick={() => onRespond(appeal.id)}
        disabled={busy !== null}
        title="Open the full response form"
        className={`${BTN} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}
      >
        Respond
      </button>
    </div>
  );
}
